import { FC, useEffect, useState } from 'react';
import { X, ShoppingBag, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCart, hasUnresolvedRxItems } from '../../../hooks/useCart';
import { CartItem } from './CartItem';
import { CouponInput } from './CouponInput';
import { OrderSummary } from './OrderSummary';
import { PrescriptionModal } from './PrescriptionModal';

interface CartDrawerProps {
    isOpen: boolean;
    onClose: () => void;
}

export const CartDrawer: FC<CartDrawerProps> = ({ isOpen, onClose }) => {
    const { t } = useTranslation('shop');
    const { data: cartData, isLoading } = useCart();
    const cart = cartData as any; // Type override for flexibility internally

    const [appliedCoupon, setAppliedCoupon] = useState<any>(null);

    const [isRendered, setIsRendered] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    const [isPrescriptionModalOpen, setIsPrescriptionModalOpen] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setIsRendered(true);
            // Double rAF ensures the DOM has updated with translate-x-full before we toggle to translate-x-0
            requestAnimationFrame(() => {
                requestAnimationFrame(() => setIsVisible(true));
            });
        } else {
            setIsVisible(false);
        }
    }, [isOpen]);

    const handleTransitionEnd = () => {
        if (!isOpen) setIsRendered(false);
    };

    // Prevent body scroll when open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen]);

    if (!isRendered) return null;

    const isEmpty = !cart?.items || cart.items.length === 0;

    // Subtotal from the hook calculation
    const subtotal = cart?.subtotal || 0;
    const discountAmount = appliedCoupon?.discountAmount || 0;
    const finalTotal = subtotal - discountAmount;

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-secondary/80 backdrop-blur-sm z-50 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                onClick={onClose}
            ></div>

            {/* Slide-over Panel */}
            <div
                className={`fixed inset-y-0 right-0 z-50 w-full md:max-w-md bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${isVisible ? 'translate-x-0' : 'translate-x-full'}`}
                onTransitionEnd={handleTransitionEnd}
            >

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 bg-white">
                    <div className="flex items-center gap-3 text-secondary">
                        <ShoppingBag size={24} className="stroke-[1.5]" />
                        <h2 className="text-xl font-bold">{t('cart.yourBag')}</h2>
                        {!isEmpty && (
                            <span className="bg-primary/10 text-primary text-sm font-bold px-2 py-0.5 rounded-full">
                                {cart.items.length}
                            </span>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body Content */}
                <div className="flex-1 overflow-y-auto bg-gray-50/50">
                    {isLoading ? (
                        <div className="flex-1 flex items-center justify-center p-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                    ) : isEmpty ? (
                        <div className="flex-1 flex flex-col items-center justify-center p-12 text-center h-full">
                            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6 text-gray-300">
                                <ShoppingBag size={48} strokeWidth={1} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('cart.emptyBagTitle')}</h3>
                            <p className="text-gray-500 mb-8 max-w-[250px]">{t('cart.emptyBagDesc')}</p>
                            <button
                                onClick={onClose}
                                className="px-8 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                            >
                                {t('cart.continueShopping')}
                            </button>
                        </div>
                    ) : (
                        <div className="flex flex-col">
                            {/* Items List */}
                            <div className="px-6 py-2 bg-white flex flex-col">
                                {cart.items.map((item: any) => (
                                    <CartItem key={item.id} item={item} />
                                ))}
                            </div>

                            {/* Coupon & Summary Wrapper */}
                            <div className="p-6 flex flex-col gap-6">
                                <CouponInput
                                    cartId={cart.id}
                                    onApplySuccess={setAppliedCoupon}
                                    onClear={() => setAppliedCoupon(null)}
                                    appliedCouponCode={appliedCoupon?.coupon?.code}
                                />

                                <OrderSummary
                                    subtotal={subtotal}
                                    discount={discountAmount}
                                // freeShippingThreshold={1500} // Hardcode threshold for now if desired, or fetch from shipping settings
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Sticky CTA */}
                {!isEmpty && (
                    <div className="p-6 border-t border-gray-100 bg-white shadow-[0_-10px_30px_rgba(0,0,0,0.05)] relative z-10">
                        <button
                            className="w-full flex items-center justify-between px-6 py-4 bg-primary text-white rounded-xl hover:bg-primary/90 transition-all group shadow-lg shadow-primary/20"
                            onClick={() => {
                                if (hasUnresolvedRxItems(cart)) {
                                    setIsPrescriptionModalOpen(true);
                                } else {
                                    window.location.href = '/checkout';
                                }
                            }}
                        >
                            <span className="font-bold text-lg">{t('cart.checkout')}</span>
                            <div className="flex items-center gap-3">
                                <span className="font-bold text-lg">EGP {finalTotal.toLocaleString()}</span>
                                <ArrowRight size={20} className="transform group-hover:translate-x-1 transition-transform" />
                            </div>
                        </button>
                    </div>
                )}

            </div>

            <PrescriptionModal
                isOpen={isPrescriptionModalOpen}
                onClose={() => setIsPrescriptionModalOpen(false)}
                cart={cart}
                onCompleteFlow={() => {
                    setIsPrescriptionModalOpen(false);
                    window.location.href = '/checkout';
                }}
            />
        </>
    );
};
